import api_fetch from '../utils/api_fetch';
import { storageHandler } from '../utils/storage_handler';

/**
 * Creates a standardized service client with common CRUD operations
 * and centralized API call patterns.
 *
 * @param {Object} config - Service configuration
 * @param {string} config.baseEndpoint - Base API endpoint (e.g., '/v1/purchase-orders')
 * @param {string} [config.searchEndpoint] - Search endpoint (defaults to baseEndpoint/search)
 * @param {Object} [config.customMethods] - Additional service-specific methods
 *
 * @returns {Object} Service client with standard and custom methods
 *
 * @example
 * const purchaseOrdersClient = createServiceClient({
 *   baseEndpoint: '/v1/purchase-orders',
 *   customMethods: {
 *     getTrackingByPO: (client) => async (id) => {
 *       return client.rawGet(`/v1/purchase-orders/${id}/tracking`);
 *     }
 *   }
 * });
 */
export default function createServiceClient(config) {
  const {
    baseEndpoint,
    searchEndpoint = `${baseEndpoint}/search`,
    customMethods = {}
  } = config;

  /**
   * Helper: Get authentication headers
   */
  const getAuthHeaders = () => ({
    Authorization: `Bearer ${storageHandler.localStorageGet('token')}`
  });

  /**
   * Helper: Calculate pagination offset from page number
   */
  const getPaginationOffset = (page = 1, limit = 20) => {
    return {
      offset: (page - 1) * limit,
      limit
    };
  };

  /**
   * Helper: Build URLSearchParams from object, filtering out empty values
   */
  const buildQueryParams = (params = {}) => {
    const queryParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        queryParams.append(key, value.toString());
      }
    });

    return queryParams;
  };

  /**
   * Helper: Apply filters to URLSearchParams, skipping falsy values
   */
  const applyFilters = (params, filters = {}) => {
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        params.append(key, value);
      }
    });
  };

  /**
   * Helper: Apply sorting parameters if valid
   */
  const applySorting = (params, sortBy = null, sortOrder = null) => {
    if (sortBy && sortOrder && sortBy !== 'default' && sortOrder !== 'default') {
      params.append('sort_by', sortBy);
      params.append('sort_order', sortOrder);
    }
  };

  // Create base client object
  const client = {
    /**
     * Get paginated list of resources
     * GET {baseEndpoint}?offset={offset}&limit={limit}
     *
     * @param {number} page - Page number (1-indexed)
     * @param {number} limit - Items per page
     * @returns {Promise<Object>} Response with data and pagination
     */
    async getList(page = 1, limit = 20) {
      const { offset, limit: lim } = getPaginationOffset(page, limit);
      const params = buildQueryParams({ offset, limit: lim });

      const response = await api_fetch.get(
        `${baseEndpoint}?${params.toString()}`,
        getAuthHeaders(),
        {}
      );

      return response.json();
    },

    /**
     * Search with filters, sorting, and pagination
     * GET {searchEndpoint}?offset={offset}&limit={limit}&...filters&sort_by={sortBy}&sort_order={sortOrder}
     *
     * @param {Object} filters - Filter key-value pairs
     * @param {number} page - Page number (1-indexed)
     * @param {string} sortBy - Sort field name
     * @param {string} sortOrder - Sort order ('asc' or 'desc')
     * @param {number} limit - Items per page
     * @returns {Promise<Object>} Response with data and pagination
     */
    async search(filters = {}, page = 1, sortBy = null, sortOrder = null, limit = 20) {
      const { offset, limit: lim } = getPaginationOffset(page, limit);
      const params = new URLSearchParams();

      params.append('limit', lim.toString());
      params.append('offset', offset.toString());

      applyFilters(params, filters);
      applySorting(params, sortBy, sortOrder);

      const response = await api_fetch.get(
        `${searchEndpoint}?${params.toString()}`,
        getAuthHeaders(),
        {}
      );

      return response.json();
    },

    /**
     * Get single resource by ID
     * GET {baseEndpoint}/{id}
     *
     * @param {string|number} id - Resource ID
     * @returns {Promise<Object>} Resource data
     */
    async getById(id) {
      const response = await api_fetch.get(
        `${baseEndpoint}/${id}`,
        getAuthHeaders(),
        {}
      );

      return response.json();
    },

    /**
     * Create new resource
     * POST {baseEndpoint}
     *
     * @param {Object} data - Resource data
     * @returns {Promise<Object>} Created resource
     */
    async create(data) {
      const response = await api_fetch.post(
        baseEndpoint,
        getAuthHeaders(),
        {},
        '',
        data
      );

      return response.json();
    },

    /**
     * Update existing resource
     * PUT {baseEndpoint}/{id}
     *
     * @param {string|number} id - Resource ID
     * @param {Object} data - Updated resource data
     * @returns {Promise<Object>} Updated resource
     */
    async update(id, data) {
      const response = await api_fetch.put(
        `${baseEndpoint}/${id}`,
        getAuthHeaders(),
        {},
        '',
        data
      );

      return response.json();
    },

    /**
     * Delete resource
     * DELETE {baseEndpoint}/{id}
     *
     * @param {string|number} id - Resource ID
     * @returns {Promise<Object>} Deletion result
     */
    async delete(id) {
      const response = await api_fetch.delete(
        `${baseEndpoint}/${id}`,
        getAuthHeaders(),
        {},
        '',
        {}
      );

      return response.json();
    },

    /**
     * Low-level GET request for custom endpoints
     *
     * @param {string} url - Full URL
     * @param {Object} additionalHeaders - Additional headers to merge
     * @returns {Promise<Object>} Response JSON
     */
    async rawGet(url, additionalHeaders = {}) {
      const response = await api_fetch.get(
        url,
        { ...getAuthHeaders(), ...additionalHeaders },
        {}
      );

      return response.json();
    },

    /**
     * Low-level POST request for custom endpoints
     *
     * @param {string} url - Full URL
     * @param {Object} data - Request body
     * @param {Object} additionalHeaders - Additional headers to merge
     * @returns {Promise<Object>} Response JSON
     */
    async rawPost(url, data, additionalHeaders = {}) {
      const response = await api_fetch.post(
        url,
        { ...getAuthHeaders(), ...additionalHeaders },
        {},
        '',
        data
      );

      return response.json();
    },

    /**
     * Low-level PUT request for custom endpoints
     *
     * @param {string} url - Full URL
     * @param {Object} data - Request body
     * @param {Object} additionalHeaders - Additional headers to merge
     * @returns {Promise<Object>} Response JSON
     */
    async rawPut(url, data, additionalHeaders = {}) {
      const response = await api_fetch.put(
        url,
        { ...getAuthHeaders(), ...additionalHeaders },
        {},
        '',
        data
      );

      return response.json();
    },

    /**
     * Expose helpers for use in custom methods
     */
    helpers: {
      getAuthHeaders,
      getPaginationOffset,
      buildQueryParams,
      applyFilters,
      applySorting
    }
  };

  // Attach custom methods
  Object.entries(customMethods).forEach(([methodName, methodFactory]) => {
    client[methodName] = methodFactory(client);
  });

  return client;
}
